import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EnviarEvaluacionInput, instanciaPasoService, TemaProcesoInput } from '../services/instanciaPasoService';

const INSTANCIAS_KEY = ['instancias'];

/**
 * "Mis Procesos" en dos consultas en vez de una.
 *
 * Antes la pantalla pedía **todos** los procesos visibles y repartía en el cliente.
 * Para un coordinador eso son miles de filas —con su comisión y sus evaluaciones—
 * y la vista quedaba en blanco hasta que llegara la última: incluso un profesor con
 * pocos estudiantes esperaba por su historial completo.
 *
 * Ahora son tres consultas independientes, y cada trozo se pinta apenas llega:
 * lo propio (corto), lo ajeno del semestre que se esté mirando, y los semestres
 * disponibles. Cambiar de semestre solo vuelve a pedir el segundo, y React Query
 * deja cacheado el anterior.
 */
export function useProcesosMios(enabled = true) {
  return useQuery({ queryKey: [...INSTANCIAS_KEY, 'mios'], queryFn: instanciaPasoService.mios, enabled });
}

/** Procesos donde guío, coguío o evalúo. `periodo` vacío = lo que está en curso. */
export function useProcesosStaff(periodo: string, enabled = true) {
  return useQuery({
    queryKey: [...INSTANCIAS_KEY, 'staff', periodo],
    queryFn: () => instanciaPasoService.staff(periodo),
    // Al cambiar de semestre se conserva lo anterior a la vista mientras carga, en vez
    // de vaciar la tabla y volver a montarla.
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** Semestres con procesos míos, para el selector (ya no se deducen de la lista). */
export function usePeriodosProcesos(enabled = true) {
  return useQuery({ queryKey: [...INSTANCIAS_KEY, 'periodos'], queryFn: instanciaPasoService.periodos, enabled });
}

/** Sube el documento de un proceso; refresca procesos y la ficha del estudiante. */
export function useSubirDocumento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archivo }: { id: number; archivo: File }) => instanciaPasoService.subirDocumento(id, archivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTANCIAS_KEY });
      qc.invalidateQueries({ queryKey: ['estudiante'] });
    },
  });
}

/** El coordinador pone o cambia el tema de un proceso (y de sus otros procesos abiertos sin tema). */
export function useEditarTemaProceso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: TemaProcesoInput }) => instanciaPasoService.editarTema(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTANCIAS_KEY });
      qc.invalidateQueries({ queryKey: ['estudiante'] });
      qc.invalidateQueries({ queryKey: ['estudiantes'] });
      qc.invalidateQueries({ queryKey: ['comision'] });
    },
  });
}

/** El coordinador reasigna/quita el tema de un proceso; refresca ficha, procesos y temas. */
export function useReasignarTema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, temaId }: { id: number; temaId: number | null }) => instanciaPasoService.reasignarTema(id, temaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INSTANCIAS_KEY });
      qc.invalidateQueries({ queryKey: ['estudiante'] });
      qc.invalidateQueries({ queryKey: ['temas'] });
    },
  });
}

function useRefrescarProcesos() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: INSTANCIAS_KEY });
    qc.invalidateQueries({ queryKey: ['estudiante'] });
  };
}

/** Comisión de un proceso (coordinador). */
export function useComision(id: number, enabled: boolean) {
  return useQuery({ queryKey: ['comision', id], queryFn: () => instanciaPasoService.getComision(id), enabled });
}

export function useConfirmarComision() {
  const refrescar = useRefrescarProcesos();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ids }: { id: number; ids: number[] }) => instanciaPasoService.confirmarComision(id, ids),
    onSuccess: (_d, v) => {
      refrescar();
      qc.invalidateQueries({ queryKey: ['comision', v.id] });
      // La pantalla de Comisiones cuenta y muestra lo mismo por cohorte.
      qc.invalidateQueries({ queryKey: ['cohortes-comision'] });
      qc.invalidateQueries({ queryKey: ['distribucion-actual'] });
    },
  });
}

/** Formulario de evaluación de un proceso (integrante de la comisión). */
export function useEvaluacion(id: number, enabled: boolean) {
  return useQuery({ queryKey: ['evaluacion', id], queryFn: () => instanciaPasoService.getEvaluacion(id), enabled });
}

export function useEnviarEvaluacion() {
  const refrescar = useRefrescarProcesos();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: number } & EnviarEvaluacionInput) => instanciaPasoService.enviarEvaluacion(id, input),
    onSuccess: (_d, v) => {
      refrescar();
      qc.invalidateQueries({ queryKey: ['evaluacion', v.id] });
    },
  });
}

/** Cambia el estado de un proceso a mano. Refresca lo mismo que publicar. */
export function useCambiarEstado() {
  const refrescar = useRefrescarProcesos();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, estado }: { id: number; estado: string }) => instanciaPasoService.cambiarEstado(id, estado),
    onSuccess: () => {
      refrescar();
      qc.invalidateQueries({ queryKey: ['estudiante'] });
    },
  });
}

export function usePublicarNota() {
  const refrescar = useRefrescarProcesos();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, anticipada, resultado }: { id: number; anticipada?: boolean; resultado?: string }) =>
      instanciaPasoService.publicarNota(id, anticipada, resultado),
    onSuccess: () => {
      refrescar();
      qc.invalidateQueries({ queryKey: ['estudiante'] });
    },
  });
}

export function useInstancia(id: number) {
  return useQuery({
    queryKey: [...INSTANCIAS_KEY, id],
    queryFn: () => instanciaPasoService.get(id),
    enabled: Number.isFinite(id),
  });
}
